import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { StateService } from '@uirouter/angular';
import { FavoritesService, Bookmark } from '../favorites.service';
import { StateHelperService } from '../../../core/services/core.ui.state.service';

@Component({
    selector: 'favorites-list',
    template: require('./favorites-list.component.html')
})
export class FavoritesListComponent implements OnInit, OnDestroy {
    favorites: Bookmark[] = [];

    // Drag and drop state
    draggedItem: Bookmark | null = null;
    dragOverIndex: number = -1;

    private subscription: Subscription;

    constructor(
        public favoritesService: FavoritesService,
        private stateService: StateService,
        private stateHelperService: StateHelperService
    ) {}

    ngOnInit(): void {
        // Initialize the service and load favorites
        this.favoritesService.init().then(() => {
            this.favorites = this.favoritesService.getFavorites();
        });

        // Subscribe to favorites changes to update list
        this.subscription = this.favoritesService.favoritesChanged$.subscribe(() => {
            this.favorites = this.favoritesService.getFavorites();
        });
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    /**
     * Navigate to a bookmarked page using SPA navigation when possible
     */
    navigateTo(bookmark: Bookmark): void {
        this.stateHelperService.navigateByUrl(bookmark.url || '');
    }

    /**
     * Remove a favorite from the list
     */
    removeFavorite(bookmark: Bookmark, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.favoritesService.removeFavorite(bookmark.id);
    }

    /**
     * Get icon class for bookmark section
     */
    getIcon(section: string): string {
        return this.favoritesService.getSectionIcon(section);
    }

    // ---- Drag and Drop Methods ----

    /**
     * Called when drag starts on an item
     */
    onDragStart(event: DragEvent, bookmark: Bookmark, index: number): void {
        this.draggedItem = bookmark;
        // Set drag data
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', bookmark.id);
        }
        // Adds a delay to allow the drag image to be captured
        setTimeout(() => {
            const target = event.target as HTMLElement;
            target.classList.add('dragging');
        }, 0);
    }

    /**
     * Called when drag ends
     */
    onDragEnd(event: DragEvent): void {
        const target = event.target as HTMLElement;
        target.classList.remove('dragging');
        this.draggedItem = null;
        this.dragOverIndex = -1;
    }

    /**
     * Called when dragging over a drop target
     */
    onDragOver(event: DragEvent, index: number): void {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
        this.dragOverIndex = index;
    }

    /**
     * Called when drag leaves a drop target
     */
    onDragLeave(event: DragEvent): void {
        this.dragOverIndex = -1;
    }

    /**
     * Called when item is dropped
     */
    onDrop(event: DragEvent, targetIndex: number): void {
        event.preventDefault();
        
        if (!this.draggedItem) return;

        const sourceIndex = this.favorites.findIndex(f => f.id === this.draggedItem!.id);
        if (sourceIndex === -1 || sourceIndex === targetIndex) {
            this.dragOverIndex = -1;
            return;
        }

        // Create new order array
        const newOrder = this.favorites.map(f => f.id);
        
        // Remove from source position
        newOrder.splice(sourceIndex, 1);
        
        // Insert at target position
        const insertIndex = sourceIndex < targetIndex ? targetIndex : targetIndex;
        newOrder.splice(insertIndex, 0, this.draggedItem.id);

        // Call service to reorder and persist
        this.favoritesService.reorderFavorites(newOrder);

        this.dragOverIndex = -1;
        this.draggedItem = null;
    }
}
